import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { locationsAPI, adminAPI } from '../../../api/bookings';
import { DEFAULT_FORM_DATA, DEFAULT_OPERATING_HOURS } from './constants';
import { withBreakUids, stripBreakUids, genBreakUid } from './utils';

export function useLocationsManagement() {
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [pricingRules, setPricingRules] = useState([]);

  // Dialog + form state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({ ...DEFAULT_FORM_DATA });

  // ==================== LOADERS ====================
  const loadLocations = useCallback(async (keepSelectedId = null) => {
    try {
      const locs = await locationsAPI.getAllLocations();
      setLocations(locs);

      // Preserve / refresh the selected location across reloads.
      setSelectedLocation((current) => {
        if (keepSelectedId) {
          return locs.find((l) => l.id === keepSelectedId) || current;
        }
        if (!current && locs.length > 0) return locs[0];
        if (current) {
          return locs.find((l) => l.id === current.id) || current;
        }
        return current;
      });
    } catch (_error) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPricingRules = useCallback(async (locationId) => {
    try {
      const rules = await adminAPI.getPricingRules(locationId);
      setPricingRules(rules);
    } catch (error) {
      console.error('Failed to load pricing rules:', error);
    }
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    if (selectedLocation) loadPricingRules(selectedLocation.id);
  }, [selectedLocation, loadPricingRules]);

  // ==================== FORM ====================
  const resetForm = useCallback(() => {
    setFormData({ ...DEFAULT_FORM_DATA });
    setEditingLocation(null);
    setActiveTab('details');
  }, []);

  const openCreate = useCallback(() => {
    resetForm();
    setIsDialogOpen(true);
  }, [resetForm]);

  const openEdit = useCallback((location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      postal_code: location.postal_code || '',
      phone: location.phone || '',
      email: location.email || '',
      notification_email: location.notification_email || '',
      bank_name: location.bank_name || '',
      bank_account: location.bank_account || '',
      courts_count: location.courts_count || 5,
      operating_hours: withBreakUids(location.operating_hours || DEFAULT_OPERATING_HOURS),
      sports: location.sports || ['badminton', 'pickleball'],
      amenities: location.amenities || [],
      description: location.description || '',
      active: location.active !== false,
    });
    setActiveTab('details');
    setIsDialogOpen(true);
  }, []);

  const handleDialogChange = useCallback((open) => {
    setIsDialogOpen(open);
    if (!open) resetForm();
  }, [resetForm]);

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateOperatingHours = useCallback((day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: { ...prev.operating_hours[day], [field]: value },
      },
    }));
  }, []);

  const updateDayBreaks = useCallback((day, mutator) => {
    setFormData((prev) => {
      const current = prev.operating_hours[day]?.breaks || [];
      return {
        ...prev,
        operating_hours: {
          ...prev.operating_hours,
          [day]: { ...prev.operating_hours[day], breaks: mutator(current) },
        },
      };
    });
  }, []);

  const addBreak = useCallback((day) => {
    updateDayBreaks(day, (breaks) => [
      ...breaks,
      { start: '12:00', end: '14:00', reason: '', _uid: genBreakUid() },
    ]);
  }, [updateDayBreaks]);

  const updateBreakField = useCallback((day, idx, field, value) => {
    updateDayBreaks(day, (breaks) => {
      const next = [...breaks];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  }, [updateDayBreaks]);

  const removeBreak = useCallback((day, idx) => {
    updateDayBreaks(day, (breaks) => breaks.filter((_, i) => i !== idx));
  }, [updateDayBreaks]);

  const toggleAmenity = useCallback((amenity) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity],
    }));
  }, []);

  const toggleSport = useCallback((sport) => {
    setFormData((prev) => ({
      ...prev,
      sports: prev.sports.includes(sport)
        ? prev.sports.filter((s) => s !== sport)
        : [...prev.sports, sport],
    }));
  }, []);

  // ==================== MUTATIONS ====================
  const handleSaveLocation = useCallback(async () => {
    if (!formData.name.trim()) { toast.error('Location name is required'); return; }
    if (!formData.address.trim()) { toast.error('Address is required'); return; }

    const payload = {
      ...formData,
      operating_hours: stripBreakUids(formData.operating_hours),
    };

    try {
      if (editingLocation) {
        await locationsAPI.updateLocation(editingLocation.id, payload);
        toast.success('Location updated successfully');
        setIsDialogOpen(false);
        resetForm();
        await loadLocations(editingLocation.id);
      } else {
        const created = await locationsAPI.createLocation(payload);
        toast.success('Location created successfully');
        setIsDialogOpen(false);
        resetForm();
        await loadLocations(created.id);
      }
    } catch (error) {
      console.error('Failed to save location:', error);
      toast.error(error.response?.data?.detail || 'Failed to save location');
    }
  }, [formData, editingLocation, resetForm, loadLocations]);

  const handleDeleteLocation = useCallback(async (locationId) => {
    const location = locations.find((l) => l.id === locationId);
    const locationName = location?.name || 'this location';

    const confirmMessage = `⚠️ PERMANENT DELETION WARNING ⚠️

You are about to PERMANENTLY DELETE "${locationName}".

This will remove:
✗ The location record
✗ All associated courts
✗ All pricing rules

This action CANNOT be undone!

Type "DELETE" to confirm:`;

    const userInput = window.prompt(confirmMessage);
    if (userInput !== 'DELETE') {
      if (userInput !== null) {
        toast.error('Deletion cancelled. You must type "DELETE" to confirm.');
      }
      return;
    }

    try {
      await locationsAPI.deleteLocation(locationId);
      toast.success(`${locationName} permanently deleted`);
      if (selectedLocation?.id === locationId) setSelectedLocation(null);
      await loadLocations();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  }, [locations, selectedLocation, loadLocations]);

  const goToPricing = useCallback(() => navigate('/admin/pricing'), [navigate]);

  return {
    // data
    locations, loading, selectedLocation, setSelectedLocation, pricingRules,

    // dialog + form
    isDialogOpen, handleDialogChange,
    editingLocation, activeTab, setActiveTab,
    formData, updateField,

    // actions
    openCreate, openEdit,
    handleSaveLocation, handleDeleteLocation,
    goToPricing,

    // operating hours + breaks
    updateOperatingHours, addBreak, updateBreakField, removeBreak,

    // sports + amenities
    toggleAmenity, toggleSport,
  };
}
